import { Injectable } from '@angular/core';
import { isEmptyArray, isEmptyObject, isStringEmpty } from '@common/helpers';
import {
  MailDestinationType,
  PassengerInformationInputModalEditArea,
  PhoneDestinationType,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { ArrivalFlightInformation, PassengerType } from '@common/interfaces';
import { FrequentFlyerCards } from '@common/interfaces/frequent-flyer-cards';
import {
  BasicInformationData,
  ContactInformationData,
  MileageProgramInformationData,
  PassengerInformationData,
  PassengerInformationParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-information.interface';
import { PassportInformationService } from '@common/services/my-booking/passenger-information-input-modal/passport-information.service';
import { convertStringToDate } from '@lib/helpers';
import { StaticMsgPipe } from '@lib/pipes/static-msg/static-msg.pipe';
import { AswMasterService } from '@lib/services/asw-master/asw-master.service';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { BehaviorSubject } from 'rxjs';
import { IFlyMemberInfo } from 'src/sdk-member';
import { FrequentFlyerCard, GetOrderResponseDataOrderType } from 'src/sdk-servicing';
import { GetOrderResponseData } from 'src/sdk-servicing/model/getOrderResponseData';
import { GetOrderResponseDataAir } from 'src/sdk-servicing/model/getOrderResponseDataAir';
import { GetOrderResponseDataContactsRepresentative } from 'src/sdk-servicing/model/getOrderResponseDataContactsRepresentative';
import { GetOrderResponseDataContactsRepresentativePhonesInner } from 'src/sdk-servicing/model/getOrderResponseDataContactsRepresentativePhonesInner';
import { Traveler } from 'src/sdk-servicing/model/traveler';
import { TravelerNamesInner } from 'src/sdk-servicing/model/travelerNamesInner';
import { ArrivalDepartureNoticeInformationService } from './arrival-departure-notice-information.service';
import { ImmigrationInformationService } from './immigration-information.service';
import { PassengerInformationInputModalCommonService } from './passenger-information-input-modal.common.service';
import { SupportInformationService } from './support-information.service';

/**
 * 搭乗者情報ブロック service
 */
@Injectable()
export class PassengerInformationService {
  public firstTravelerId: string = '';
  public passengerSMSDestinationChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private readonly _common: CommonLibService,
    private readonly _staticMsg: StaticMsgPipe,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService,
    private readonly _arrivalDepartureNoticeInfoService: ArrivalDepartureNoticeInformationService,
    private readonly _immigrationInfoService: ImmigrationInformationService,
    private readonly _passportInfoService: PassportInformationService,
    private readonly _supportInfoService: SupportInformationService,
    private readonly _aswMasterService: AswMasterService
  ) {}

  /**
   * 搭乗者基本情報 初期値作成
   * @param traveler 搭乗者情報
   * @returns 搭乗者基本情報 初期値
   */
  createPassengerBasicInformationData(traveler: Traveler): BasicInformationData {
    return {
      title: traveler.names?.[0]?.title ?? '',
      firstName: traveler.names?.[0]?.firstName ?? '',
      middleName: traveler.names?.[0]?.middleName ?? '',
      lastName: traveler.names?.[0]?.lastName ?? '',
      selectYmd: traveler.dateOfBirth ? convertStringToDate(traveler.dateOfBirth) : null,
      gender: traveler.gender ?? '',
      age: traveler.age ?? '',
      isError: false,
    };
  }

  /**
   * 搭乗者連絡先情報 初期値の作成
   * @param traveler 搭乗者情報
   * @param countries 国リスト
   * @param contactsTraveler FIXME: Đã hardcode interface ở sdk. chờ version sdk mới
   * @param representative 代表者情報
   * @returns 搭乗者連絡先情報 初期値
   */
  createPassengerContactInformationData(
    contactsTraveler: any,
    representative: GetOrderResponseDataContactsRepresentative,
    isNotUsContactRequired: boolean
  ): ContactInformationData {
    // (1.5.1.4) 表示用搭乗者情報を設定する。
    // 項目名: 搭乗者メールアドレス編集可否
    const isPassengerEmailEditable =
      // PNR情報取得API応答.data.contacts.[当該搭乗者情報.id].emailsの要素数≧2の場合、false
      (contactsTraveler?.emails ?? []).length < 2;

    // [8.3] 搭乗者メール送信先
    let passengerMailDestination = MailDestinationType.INDIVIDUAL;

    // [8.3.1] 搭乗者メールアドレス編集可否＝true、代表者連絡先情報.emailsの要素数＝1、かつ表示用代表者連絡先情報.emails＝搭乗者情報.搭乗者連絡先情報.搭乗者メールアドレスの場合、「代表者に送る」とする
    if (
      isPassengerEmailEditable &&
      representative?.emails?.length === 1 &&
      contactsTraveler.emails?.[0]?.isSameAsRepresentative
    ) {
      passengerMailDestination = MailDestinationType.REPRESENTATIVE;
    }

    // [8.3.2] 搭乗者メールアドレス編集可否＝true、かつ上記以外、「個人に送る」とする
    if (
      isPassengerEmailEditable &&
      (representative?.emails?.length !== 1 || !Boolean(contactsTraveler.emails?.[0]?.isSameAsRepresentative))
    ) {
      passengerMailDestination = MailDestinationType.INDIVIDUAL;
    }

    const smsCountry = this._s04CommonSvc.masterData.i18nCountries.find(
      (country) => country.international_tel_country_code === contactsTraveler.phones?.[0]?.countryPhoneExtension
    );
    let smsCountryCode = smsCountry?.country_2letter_code ?? '';

    const smsDestination = this.getPassengerSMSDestination(
      contactsTraveler,
      representative?.phones && representative?.phones?.length >= 2,
      isNotUsContactRequired
    );

    let smsCountryNumber = contactsTraveler.phones?.[0]?.countryPhoneExtension ?? '';
    if (isStringEmpty(smsCountryNumber) && this._common.isJapaneseOffice()) {
      smsCountryCode = 'JP';
      smsCountryNumber = '81';
    }

    const secondContact = contactsTraveler?.secondContact;
    const secondCountry = this._s04CommonSvc.masterData.i18nCountries.find(
      (country) => country.international_tel_country_code === secondContact?.[0]?.phones?.countryPhoneExtension
    );
    let secondCountryCode = secondCountry?.country_2letter_code ?? '';

    let secondCountryNumber = secondContact?.[0]?.phones?.countryPhoneExtension ?? '';
    if (isStringEmpty(secondCountryNumber) && this._common.isJapaneseOffice()) {
      secondCountryCode = 'JP';
      secondCountryNumber = '81';
    }

    // ※表示用搭乗者情報.搭乗者連絡先情報.搭乗者SMS番号の国番号が(＋1)の場合には優先的にアメリカ合衆国/カナダ
    smsCountryCode = smsCountry?.international_tel_country_code === '1' ? 'US' : smsCountryCode;
    // ※表示用搭乗者情報.搭乗者連絡先情報.2件目緊急連絡先.2件目緊急連絡先電話番号の国番号が(＋1)の場合には優先的にアメリカ合衆国/カナダ
    secondCountryCode = secondCountry?.international_tel_country_code === '1' ? 'US' : secondCountryCode;

    return {
      passengerMailDestination: passengerMailDestination,
      passengerMailAddress: contactsTraveler.emails?.[0]?.address ?? '',
      passengerMailAddressConfirm: contactsTraveler.emails?.[0]?.address ?? '',
      passengerSMSDestination: smsDestination,
      passengerSMSCountry: smsCountryCode,
      passengerSMSCountryNumber: smsCountryNumber,
      passengerSMSNumber: contactsTraveler.phones?.[0]?.number,
      secondContact: {
        countryCode: secondCountryCode,
        countryPhoneExtension: secondCountryNumber,
        phonesNumber: secondContact?.[0]?.phones?.number ?? '',
        ownerOfPhone: secondContact?.[0]?.ownerOfPhone ?? '',
      },
      // 重複を除いてすべて表示し
      secondContacts: secondContact?.filter(
        (d: any, index: number) =>
          index ===
          secondContact?.findIndex(
            (f: any) =>
              f?.phones?.countryPhoneExtension === d?.phones?.countryPhoneExtension &&
              f?.phones?.number === d?.phones?.number &&
              f?.ownerOfPhone === d?.ownerOfPhone
          )
      ),
      isError: false,
    };
  }

  getPassengerSMSDestination(
    contactsTraveler: any,
    isNotEditRepresentativePhone?: boolean,
    isNotUsContactRequired?: boolean
  ) {
    if (isNotUsContactRequired && Boolean(contactsTraveler?.isRefused)) {
      return PhoneDestinationType.NOT_SEND;
    }
    if ((contactsTraveler.phones ?? []).length >= 1) {
      if (contactsTraveler.phones?.[0]?.isSameAsRepresentative) {
        return PhoneDestinationType.REPRESENTATIVE;
      }
      if (contactsTraveler.phones?.[0]?.isSameAsRepresentative === false || isNotEditRepresentativePhone) {
        return PhoneDestinationType.INDIVIDUAL;
      }
    }
    return PhoneDestinationType.NOT_SEND;
  }

  /**
   * FFP情報 初期値の作成
   * @param frequentFlyerCards 予約詳細画面におけるFFP情報
   * @returns FFP情報 初期値
   */
  createMileageProgramInformationData(
    frequentFlyerCards: Array<FrequentFlyerCard>
  ): Array<MileageProgramInformationData> {
    const arrayForSort = [...this._s04CommonSvc.masterData.i18nMileagePrograms];
    const orderdI18nMileagePrograms = arrayForSort.sort((a, b) => {
      return a.display_order - b.display_order;
    });
    if (isEmptyArray(frequentFlyerCards)) {
      return [
        {
          mileageProgramName: orderdI18nMileagePrograms[0]?.carrier_code ?? '',
          mileageProgramNumber: '',
          isError: false,
        },
      ];
    }
    return (
      frequentFlyerCards.map((frequentFlyerCard) => {
        return {
          mileageProgramName: frequentFlyerCard.companyCode ?? '',
          mileageProgramNumber: frequentFlyerCard.cardNumber ?? '',
          isError: false,
        };
      }) ?? []
    );
  }

  /**
   * 搭乗者情報ブロック 初期値の作成
   * @param traveler 搭乗者情報
   * @param pnr PNR情報
   * @returns 搭乗者情報ブロック 初期値
   */
  createData(traveler: Traveler, pnr?: GetOrderResponseData): PassengerInformationData {
    const representative: GetOrderResponseDataContactsRepresentative = pnr?.contacts?.representative ?? {};
    const contactsTraveler = pnr?.contacts?.[traveler.id ?? ''] ?? {};
    const frequentFlyerCards = (pnr?.frequentFlyerCards as FrequentFlyerCards)?.[traveler.id ?? '']?.fqtv ?? [];
    const regulatoryDetails = traveler.regulatoryDetails ?? {};
    const arrivalFlights: Array<ArrivalFlightInformation> = this.getArrivalCountries(pnr);
    const orderTasks = pnr?.tasks ?? {};
    // FIXME: travelerSupports trong interface ở sdk đang là object. chờ version sdk mới
    const support = (pnr?.travelerSupports ?? ({} as any))?.[traveler.id ?? ''] ?? {};
    const isNotUsContactRequired = orderTasks?.isUsContactRequired === false;

    return {
      memberInformation: new BehaviorSubject<IFlyMemberInfo>({
        profileDetails: {},
        programDetails: [],
      }),
      travelerId: traveler.id ?? '',
      arrivalAndDepartureNotice: this._arrivalDepartureNoticeInfoService.createData(contactsTraveler),
      basicInformation: this.createPassengerBasicInformationData(traveler),
      contact: this.createPassengerContactInformationData(contactsTraveler, representative, isNotUsContactRequired),
      listOfMileageProgramInfo: this.createMileageProgramInformationData(frequentFlyerCards),
      immigration: this._immigrationInfoService.createData(regulatoryDetails, arrivalFlights, orderTasks),
      passports: this._passportInfoService.createData(regulatoryDetails),
      support: this._supportInfoService.createSupportInformationData(support),
      isError: false,
      passengerTypeCode: traveler.passengerTypeCode ?? '',
    };
  }

  /**
   * 搭乗者情報ブロック 設定値の作成
   * @param traveler 搭乗者情報
   * @param index 乗客リスト内の乗客のインデックス
   * @param pnr PNR情報
   * @returns 搭乗者情報ブロック 設定値
   */
  createParts(traveler: Traveler, index: number, pnr?: GetOrderResponseData): PassengerInformationParts {
    const travelerName = traveler.names?.[0];
    const passengerName: string = this.createPassengerFullName(index, travelerName);
    const representativePhoneType =
      pnr?.contacts?.representative?.phones?.[0]?.type ??
      GetOrderResponseDataContactsRepresentativePhonesInner.TypeEnum.M;
    const representativePhoneNumber = pnr?.contacts?.representative?.phones?.[0]?.number ?? '';
    const dateOfBirth = traveler?.regulatoryDetails?.passports?.[0]?.birthDate
      ? convertStringToDate(traveler?.regulatoryDetails?.passports?.[0]?.birthDate)
      : traveler?.dateOfBirth
      ? convertStringToDate(traveler.dateOfBirth)
      : null;
    const isEditMode: boolean = this._s04CommonSvc.isEditMode(
      PassengerInformationInputModalEditArea.TRAVELER_INPUT,
      traveler.id
    );
    const isRegistered: boolean = !isEmptyObject(traveler);
    const departureDate = this._s04CommonSvc.getDate(pnr?.air?.bounds?.[0]?.originDepartureDateTime) ?? new Date();

    const passengerTypeLabel = this._s04CommonSvc.getPassengerLabel(traveler.passengerTypeCode ?? '');
    const registrationLabel = this._s04CommonSvc.getRegistrationLabel(isEditMode, isRegistered);
    const registrationClass = this._s04CommonSvc.getRegistrationLabelClass(registrationLabel);
    const contactsTraveler = pnr?.contacts?.[traveler.id ?? ''] ?? {};
    const frequentFlyerCards = pnr?.frequentFlyerCards as FrequentFlyerCards;
    const fqtv = frequentFlyerCards?.[traveler.id ?? '']?.fqtv ?? [];
    const mileageFromMaster = this._s04CommonSvc.masterData.i18nMileagePrograms.find(
      (mileage) => mileage.carrier_code === fqtv?.[0]?.companyCode
    );

    // マイレージプログラム名取得可否
    const isMileageProgramAvailability =
      isEmptyArray(fqtv) || // PNR情報取得API応答.frequentFlyerCards.[当該搭乗者情報.id].companyCode(以降、当該搭乗者キャリアコード)が存在する場合のみ設定する。
      (fqtv.length > 0 &&
        // 当該搭乗者キャリアコード＝キャリアードとなるASWDB(マスタ)のマイレージプログラム(国際化).マイレージプログラム名が取得できた場合、true
        !isEmptyObject(mileageFromMaster));

    // FFP情報編集可否
    // PNR情報取得API応答.data.frequentFlyerCards.[当該搭乗者情報.id].fqtvの要素数≧2、かつPNR情報取得API応答.data.orderEligibilities.travelerInput.isFrequentFlyerCardEditable = false、false 上記以外の場合、true
    const isMileageProgramEditable: boolean = !(
      fqtv.length >= 2 || pnr?.orderEligibilities?.travelerInput?.isFrequentFlyerCardEditable === false
    );

    // FIXME: Fix notification parts, remove unused
    // [1.13] 発着通知連絡先情報
    const isDisplayArrivalDepartureNotify =
      // PNR情報取得API応答.data.orderEligibilities.travelerInput.departureArrivalNotification.isEligible = trueの場合、表示する。
      pnr?.orderEligibilities?.travelerInput?.departureArrivalNotification?.isEligible ?? false;

    // 搭乗者メールアドレス編集可否
    // PNR情報取得API応答.data.contacts.[当該搭乗者情報.id].emailsの要素数≧2の場合、false 上記以外、true
    const isPassengerEmailEditable = contactsTraveler.emails && contactsTraveler.emails.length < 2;

    // 搭乗者SMS情報編集可否
    // PNR情報取得API応答.data.contacts.[当該搭乗者情報.id].phonesの要素の要素数≧2の場合、false 上記以外、true
    const isPassengerSmsEditable = contactsTraveler.phones && contactsTraveler.phones.length < 2;

    // 2件目緊急連絡情報編集可否
    // 当該搭乗者2件目緊急連絡の要素数≧2の場合、false 上記以外、true
    const isSecondContactEditable = contactsTraveler.secondContact && contactsTraveler.secondContact.length < 2;

    const channelType = pnr?.orderType?.channelType;

    return {
      isOpen: isEditMode,
      isDisplay: true,
      isDisplayBasicInformation: false,
      isDisplayBasicReservation: false,
      isDisplayArrivalDepartureNotify: isDisplayArrivalDepartureNotify,
      isPassengerEmailEditable: isPassengerEmailEditable,
      isPassengerSmsEditable: isPassengerSmsEditable,
      isSecondContactEditable: isSecondContactEditable,
      isSecondContactRegistAfterReservation: false,
      registrationLabel: registrationLabel,
      registrationClass: registrationClass,
      representativePhoneType: representativePhoneType,
      representativePhoneNumber: representativePhoneNumber,
      isOpenBasicReservationDetails: false,
      isMileageProgramAvailability: isMileageProgramAvailability,
      isMileageProgramEditable: isMileageProgramEditable,
      passengerName: passengerName,
      passengerTypeLabel: passengerTypeLabel,
      // [1.10] 以下の処理で画面.搭乗者基本情報生年月日のプルダウンに日付範囲を設定する。
      ...this.createPassengerSelectDate(traveler, departureDate, dateOfBirth, channelType),
      ...this.createPassengerAdditionalParts(traveler, contactsTraveler, departureDate, pnr),
    };
  }

  /**
   * 乗客のフルネームを作成する
   * @param passengerIndex 乗客リスト内の乗客のインデックス
   * @param travelerName 名前情報
   * @returns 乗客のフルネーム
   */
  createPassengerFullName(passengerIndex: number, travelerName?: TravelerNamesInner) {
    if (!isStringEmpty(travelerName?.firstName)) {
      const title = travelerName?.title ?? '';
      const firstName = travelerName?.firstName ?? '';
      const lastName = travelerName?.lastName ?? '';

      return `${title} ${firstName} ${lastName}`;
    }

    return this._staticMsg.transform('label.passenger.n', { '0': passengerIndex + 1 });
  }

  /**
   * asw-select-date-ymd コンポーネントの日付データを作成する
   * @param traveler 搭乗者情報
   * @param departureDate 出発日
   * @param birthDate 生年月日
   * @returns 日付データ
   */
  createPassengerSelectDate(
    traveler: Traveler,
    departureDate: Date | null,
    birthDate: Date | null,
    channelType: string | undefined
  ) {
    const domesticDcs = this._aswMasterService.getMPropertyByKey('migration', 'uiuxs2.transitionDate.domesticDcs');
    const isDomesticDcs = departureDate && domesticDcs ? new Date(domesticDcs) > departureDate : false;
    const isChannelTypeNH = channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh;
    return {
      selectDate: {
        order: 'YMD', // 年月日の順で固定
        dateFrom:
          departureDate !== null
            ? this.getDateFrom(traveler.passengerTypeCode ?? '', departureDate, isDomesticDcs, isChannelTypeNH)
            : null,
        dateTo:
          departureDate !== null
            ? this.getDateTo(traveler.passengerTypeCode ?? '', departureDate, isDomesticDcs, isChannelTypeNH)
            : null,
        initialDate:
          birthDate !== null
            ? {
                year: birthDate.getFullYear(),
                month: birthDate.getMonth() + 1,
                day: birthDate.getDate(),
              }
            : {},
      },
    };
  }

  /**
   * 乗客の追加設定を作成する
   * @param traveler 搭乗者情報
   * @param contactsTraveler FIXME: Đã hardcode interface ở sdk. chờ version sdk mới
   * @param departureDate 出発日
   * @param regulatoryDetails 入国関連情報
   * @param pnr PNR情報
   * @returns 乗客の追加設定
   */
  createPassengerAdditionalParts(
    traveler: Traveler,
    contactsTraveler: any,
    departureDate: Date | null,
    pnr?: GetOrderResponseData
  ) {
    const chanelType = pnr?.orderType?.channelType ?? '';

    return {
      arrivalDepartureNoticeInformationParts: this._arrivalDepartureNoticeInfoService.createParts(contactsTraveler),
      immigrationInformationParts: this._immigrationInfoService.createParts(traveler),
      passportInformationParts: this._passportInfoService.createParts(traveler, departureDate, chanelType),
      supportInformationParts: this._supportInfoService.createSupportInformationParts(traveler, pnr),
    };
  }

  /**
   * 搭乗者の生年月日のFromの範囲
   * @param code 搭乗者種別
   * @param departure 出発日
   * @param isDomesticDcs 国内フラグ
   * @returns 搭乗者の生年月日のFrom
   */
  public getDateFrom(code: string, departure: Date, isDomesticDcs: boolean, isChannelTypeNH: boolean) {
    const date = new Date(departure);
    switch (code) {
      case PassengerType.ADT:
        date.setFullYear(date.getFullYear() - 99);
        date.setDate(date.getDate() + 1);
        break;
      case PassengerType.B15:
        if (isChannelTypeNH) {
          date.setFullYear(date.getFullYear() - 15);
        } else {
          date.setFullYear(date.getFullYear() - 99);
          date.setDate(date.getDate() + 1);
        }
        break;
      case PassengerType.CHD:
        date.setFullYear(date.getFullYear() - 12);
        date.setDate(date.getDate() + 1);
        break;
      case PassengerType.INS:
      case PassengerType.INF:
        if (!isDomesticDcs) {
          date.setFullYear(date.getFullYear() - 2);
          date.setDate(date.getDate() + 1);
        } else {
          date.setFullYear(date.getFullYear() - 2);
        }
        break;
      default:
        date.setFullYear(1900);
        break;
    }
    return date;
  }

  /**
   * 搭乗者の生年月日のToの範囲
   * @param code 搭乗者種別
   * @param departure 出発日
   * @param isDomesticDcs
   * @returns 搭乗者の生年月日のTo
   */
  public getDateTo(code: string, departure: Date, isDomesticDcs: boolean, isChannelTypeNH: boolean) {
    const date = new Date(departure);
    switch (code) {
      case PassengerType.ADT:
      case PassengerType.B15:
        if (isChannelTypeNH) {
          date.setFullYear(date.getFullYear() - 12);
        } else {
          date.setDate(date.getDate() - 8);
        }
        break;
      case PassengerType.CHD:
        if (!isDomesticDcs) {
          date.setFullYear(date.getFullYear() - 2);
        } else {
          date.setFullYear(date.getFullYear() - 3);
        }
        break;
      case PassengerType.INS:
      case PassengerType.INF:
        date.setDate(date.getDate() - 8);
        break;
      default:
        break;
    }
    return date;
  }

  /**
   * この関数は、曜日を表す数値 (月曜日は 0、火曜日は 1、...、日曜日は 6) を受け取り、対応するラベルを返します。
   * @param day 曜日を表す数字 (0 ～ 6)
   * @returns 対応する曜日のラベル。
   */
  public getDayOfWeekLabel(day: number): string {
    const label = [
      'label.monday',
      'label.tuesday',
      'label.wednesday',
      'label.thursday',
      'label.friday',
      'label.saturday',
      'label.sunday',
    ];

    return label[day] ?? '';
  }

  /**
   * 提供された PNR 航空データから到着フライト情報のリストを取得します。
   * @param pnrAir PNR 航空データ
   * @returns 到着フライト情報の配列。
   */
  getArrivalCountries(pnr?: GetOrderResponseData): Array<ArrivalFlightInformation> {
    let result: Array<ArrivalFlightInformation> = [];

    if (pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International) {
      pnr?.air?.bounds?.forEach((bound) => {
        bound?.flights?.forEach((flight) => {
          const arrivalFlight: ArrivalFlightInformation = {
            flightId: flight.id ?? '',
            arrivalCountry: flight.arrival?.country2letterCode ?? '',
          };

          if (isStringEmpty(arrivalFlight.flightId) || isStringEmpty(arrivalFlight.arrivalCountry)) {
            return;
          }

          const isDomesticFlight = flight.isJapanDomesticFlight ?? true;

          // [10.5] 入国する国毎の情報
          // 1.当該flightsを表示する。ただし、同じ国が2回出現する場合は、最初の1つのみ表示する。
          const checkInclude = result.some(
            (item) => item.arrivalCountry === arrivalFlight.arrivalCountry && item.flightId === arrivalFlight.flightId
          );
          // 2.PNR情報取得API応答.data.tasks.<当該flights.id>.isVisaOrResidentAlienCardRegistrationRequired≠trueの場合、非表示とする。
          const hiddenFlight =
            pnr?.tasks?.[arrivalFlight.flightId]?.isVisaOrResidentAlienCardRegistrationRequired !== true;

          if (!checkInclude && !hiddenFlight && !isDomesticFlight) {
            result.push(arrivalFlight);
          }
        });
      });
    }

    return result;
  }
}
